import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { SetAvatar, SetSwitch } from '../SettingsShell/controls';
import { takenCarriers } from './insuranceDerive';
import type { Carrier, Provider } from '../../settings-practice/types';

export interface NetworkPanelModalProps {
  provider: Provider | null;
  carriers: Carrier[];
  onClose: () => void;
  onSave: (providerId: string, inNetworkCarrierIds: string[]) => void;
}

/**
 * A provider's network (spec 15 §7.3). Opened from a provider row on Insurance,
 * or from the provider's own page — the same panel from both entry points, one
 * store. Network is the one thing a provider owns as a person rather than
 * through a role.
 *
 * The header count is the toggles, so it cannot drift.
 */
export function NetworkPanelModal({ provider, carriers, onClose, onSave }: NetworkPanelModalProps) {
  const taken = useMemo(() => takenCarriers(carriers), [carriers]);
  const paused = useMemo(() => carriers.filter((c) => c.paused), [carriers]);

  const [on, setOn] = useState<string[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (provider) {
      setOn(taken.filter((c) => c.inNetworkProviderIds.includes(provider.id)).map((c) => c.id));
      setFilter('');
    }
  }, [provider, taken]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return taken;
    return taken.filter((c) => c.name.toLowerCase().includes(q) || c.payerId.toLowerCase().includes(q));
  }, [taken, filter]);

  const offCount = taken.length - on.length;

  return (
    <ViewportOverlay open={provider !== null} onClose={onClose}>
      {provider ? (
        <div
          onClick={(e) => { e.stopPropagation(); }}
          className="flex max-h-[90vh] w-[min(660px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
        >
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
            <div className="flex items-center gap-3">
              <SetAvatar initials={provider.initials} colour={provider.colour} size={44} />
              <div>
                <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">
                  {provider.elvaSays}
                </h2>
                <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
                  {provider.roles.length > 0 ? 'Provider' : 'Provider'} ·{' '}
                  <strong className="text-[var(--text-secondary)]">
                    in network with {on.length} of {taken.length}
                  </strong>{' '}
                  carriers the practice takes
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
            >
              <Icon kind="x" size={14} stroke={2} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="px-6 pt-5">
              <span className="flex items-center gap-2 rounded-[10px] border border-[var(--border-default)] px-3 py-2">
                <Icon kind="search" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
                <input
                  value={filter}
                  onChange={(e) => { setFilter(e.target.value); }}
                  placeholder="Filter carriers…"
                  aria-label="Filter carriers"
                  className="w-full bg-transparent text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
                />
              </span>
              <div className="mt-3 flex items-center justify-between">
                <span className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => { setOn(taken.map((c) => c.id)); }}
                    className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOn([]); }}
                    className="text-[12.5px] font-semibold text-[var(--text-secondary)] hover:underline"
                  >
                    Clear all
                  </button>
                </span>
                <span className="text-[12px] text-[var(--text-muted)]">{taken.length} to set</span>
              </div>
            </div>

            <div className="mt-3">
              {shown.map((c) => {
                const isOn = on.includes(c.id);
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-3 px-6 py-3"
                    style={{
                      borderBottom: '1px solid #edf0f6',
                      background: isOn ? '#F4FAF7' : undefined,
                    }}
                  >
                    <SetAvatar initials={c.initials} colour={c.colour} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold text-[var(--text-primary)]">
                        {c.name}
                      </span>
                      <span className="block font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                        Payer {c.payerId}
                      </span>
                    </span>
                    <SetSwitch
                      label={`In network with ${c.name}`}
                      checked={isOn}
                      onChange={(next) => {
                        setOn((prev) => (next ? [...prev, c.id] : prev.filter((x) => x !== c.id)));
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {paused.length > 0 ? (
              <div className="bg-[var(--surface-sunken)] px-6 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                  Paused — kept, not editable
                </div>
                <p className="m-0 mt-1.5 text-[12px] leading-[1.6] text-[var(--text-muted)]">
                  <strong className="text-[var(--text-secondary)]">Shown rather than hidden</strong>, so nobody thinks it
                  was deleted. Its network rows are held exactly as they were and come back when the contract does.
                </p>
                {paused.map((c) => (
                  <div key={c.id} className="mt-3 flex items-center gap-3 opacity-60">
                    <SetAvatar initials={c.initials} colour={c.colour} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold text-[var(--text-muted)]">
                        {c.name}
                      </span>
                      <span className="block font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                        Payer {c.payerId}
                      </span>
                    </span>
                    <Icon kind="lock" size={13} stroke={2} style={{ color: 'var(--text-muted)' }} />
                  </div>
                ))}
              </div>
            ) : null}

            {/* The consequence, stated in patients rather than counts (§7.3). */}
            <div className="bg-[var(--brand-indigo-tint)] px-6 py-4 text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
              <p className="m-0">
                <strong>What this does:</strong> a patient with one of the {on.length} switched on is offered{' '}
                {provider.elvaSays}; a patient with one of the other {offCount} is not,{' '}
                <strong>because it would cost them more.</strong>
              </p>
              <p className="m-0 mt-2.5">
                <strong>Switching off the last provider on a carrier</strong> moves that carrier to <em>Out of network</em>{' '}
                on the list behind this — the practice still takes it, and ELVA starts saying what that costs.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onSave(provider.id, on); }}
              className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
            >
              Save
            </button>
          </div>
        </div>
      ) : null}
    </ViewportOverlay>
  );
}
